import { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import SolarCalendarDateLinear from 'components/icons/SolarCalendarDateLinear';
import SolarCalendarLinear from 'components/icons/SolarCalendarLinear';
import SolarCalendarMarkLinear from 'components/icons/SolarCalendarMarkLinear';
import SolarCalendarSearchLinear from 'components/icons/SolarCalendarSearchLinear';
import SolarCloseCircleLinear from 'components/icons/SolarCloseCircleLinear';
import type { ReportDateProps } from '../types';
import {
    formatPeriodRange,
    getPresetPeriodRange,
    periodRangeToMillis,
    type ReportPeriodType,
    type ReportPresetPeriodType,
} from './periodUtils';

const PRESET_PERIODS: ReportPresetPeriodType[] = ['month', 'quarter', 'year'];

const millisToIsoDate = (millis: number): string => DT.fromMillis(millis).toISODate() || '';

const PERIOD_ICON: Record<ReportPeriodType, typeof SolarCalendarLinear> = {
    off: SolarCloseCircleLinear,
    month: SolarCalendarLinear,
    quarter: SolarCalendarMarkLinear,
    year: SolarCalendarDateLinear,
    custom: SolarCalendarSearchLinear,
};

const PeriodIcon = ({ type }: { type: ReportPeriodType }) => {
    const Icon = PERIOD_ICON[type];
    return <Icon />;
};

const ReportDate = ({
    disabled,
    setDates,
    initialFilter,
    onFilterChange,
    showOff = false,
    hideTextInTitle = false,
    alertOnFilterSet = false,
}: ReportDateProps) => {
    const { t } = useTranslation(['Reports']);
    const now = useMemo(() => DT.now(), []);

    const resolvePeriod = (period: ReportPeriodType): ReportPeriodType =>
        period === 'off' && !showOff ? 'month' : period;

    const [show, setShow] = useState(false);
    const [appliedPeriod, setAppliedPeriod] = useState<ReportPeriodType>(() =>
        resolvePeriod(initialFilter.period),
    );
    const [appliedCustomRange, setAppliedCustomRange] = useState<[number, number] | null>(
        initialFilter.customRange,
    );
    const [customStartIso, setCustomStartIso] = useState('');
    const [customEndIso, setCustomEndIso] = useState('');
    const [isCustomOpen, setIsCustomOpen] = useState(false);

    const presetRanges = useMemo(
        () =>
            PRESET_PERIODS.reduce(
                (acc, type) => {
                    const range = getPresetPeriodRange(type, now);
                    acc[type] = {
                        range,
                        label: formatPeriodRange(range.start, range.end),
                    };
                    return acc;
                },
                {} as Record<
                    ReportPresetPeriodType,
                    { range: ReturnType<typeof getPresetPeriodRange>; label: string }
                >,
            ),
        [now],
    );

    const appliedCustomLabel = useMemo(() => {
        if (!appliedCustomRange) return '';
        const [startMs, endMs] = appliedCustomRange;
        return formatPeriodRange(DT.fromMillis(startMs), DT.fromMillis(endMs));
    }, [appliedCustomRange]);

    const emitFilterChange = (
        period: ReportPeriodType,
        dates: [number, number] | null,
        customRange: [number, number] | null,
    ) => {
        setDates(dates);
        onFilterChange({ period, dates, customRange });
    };

    const applyOff = () => {
        setAppliedPeriod('off');
        setAppliedCustomRange(null);
        setIsCustomOpen(false);
        emitFilterChange('off', null, null);
        setShow(false);
    };

    const applyPreset = (type: ReportPresetPeriodType) => {
        const { start, end } = presetRanges[type].range;
        const dates = periodRangeToMillis(start, end);
        setAppliedPeriod(type);
        setAppliedCustomRange(null);
        setIsCustomOpen(false);
        emitFilterChange(type, dates, null);
        setShow(false);
    };

    const applyCustom = () => {
        if (!customStartIso || !customEndIso) return;
        const start = DT.fromISO(customStartIso, { zone: 'local' });
        const end = DT.fromISO(customEndIso, { zone: 'local' });
        const [from, to] = start <= end ? [start, end] : [end, start];
        const dates: [number, number] = [from.startOf('day').toMillis(), to.endOf('day').toMillis()];

        setAppliedPeriod('custom');
        setAppliedCustomRange(dates);
        emitFilterChange('custom', dates, dates);
        setShow(false);
    };

    const openCustom = () => {
        setIsCustomOpen(true);
        if (appliedPeriod === 'custom' && appliedCustomRange) {
            const [startMs, endMs] = appliedCustomRange;
            setCustomStartIso(millisToIsoDate(startMs));
            setCustomEndIso(millisToIsoDate(endMs));
            return;
        }
        const monthRange = presetRanges.month.range;
        setCustomStartIso(monthRange.start.toISODate() || '');
        setCustomEndIso(monthRange.end.toISODate() || '');
    };

    const toggleLabel = useMemo(() => {
        if (appliedPeriod === 'off') return t('dateFilter.off');
        if (appliedPeriod === 'custom') {
            return appliedCustomLabel || t('dateFilter.custom');
        }
        return t(`dateFilter.${appliedPeriod}`);
    }, [appliedPeriod, appliedCustomLabel, t]);

    const filterIsSet = appliedPeriod !== 'off';
    const toggleVariant = alertOnFilterSet ? (filterIsSet ? 'danger' : 'primary') : 'secondary';

    useEffect(() => {
        setAppliedPeriod(resolvePeriod(initialFilter.period));
        setAppliedCustomRange(initialFilter.customRange);
    }, [initialFilter.period, initialFilter.customRange, showOff]);

    const renderPeriodItem = (
        type: ReportPeriodType,
        title: string,
        subtitle: string,
        onSelect: () => void,
    ) => (
        <Dropdown.Item
            as="button"
            key={type}
            onClick={onSelect}
            disabled={disabled}
            active={appliedPeriod === type}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon">
                    <PeriodIcon type={type} />
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className="fw-medium">{title}</div>
                    <small className="text-muted">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    return (
        <Dropdown show={show} onToggle={(next) => !disabled && setShow(!!next)} autoClose="outside">
            <Dropdown.Toggle
                size="sm"
                variant={toggleVariant}
                className={
                    hideTextInTitle
                        ? 'fs-14 px-2 ms-1 d-inline-flex align-items-center'
                        : 'd-flex align-items-center'
                }
                disabled={disabled}
                id="report-date-filter"
                title={toggleLabel}
            >
                <span className={`d-inline-flex align-items-center report-date-toggle-icon${hideTextInTitle ? '' : ' me-1'}`}>
                    <PeriodIcon type={appliedPeriod} />
                </span>
                {!hideTextInTitle && (
                    <span className="text-truncate" style={{ maxWidth: 220 }}>
                        {toggleLabel}
                    </span>
                )}
            </Dropdown.Toggle>

            <Dropdown.Menu className="p-2 shadow-sm" style={{ minWidth: 280 }}>
                {showOff && (
                    <>
                        {renderPeriodItem('off', t('dateFilter.off'), t('dateFilter.offHint'), applyOff)}
                        <Dropdown.Divider className="my-2" />
                    </>
                )}

                {renderPeriodItem('month', t('dateFilter.month'), presetRanges.month.label, () =>
                    applyPreset('month'),
                )}
                {renderPeriodItem('quarter', t('dateFilter.quarter'), presetRanges.quarter.label, () =>
                    applyPreset('quarter'),
                )}
                {renderPeriodItem('year', t('dateFilter.year'), presetRanges.year.label, () =>
                    applyPreset('year'),
                )}

                <Dropdown.Divider className="my-2" />

                {renderPeriodItem(
                    'custom',
                    t('dateFilter.custom'),
                    appliedPeriod === 'custom' && appliedCustomLabel
                        ? appliedCustomLabel
                        : t('dateFilter.customHint'),
                    openCustom,
                )}

                {isCustomOpen && (
                    <div className="px-2 pt-2 pb-1 border-top" onClick={(e) => e.stopPropagation()}>
                        <div className="mb-2">
                            <label className="form-label fs-12 text-muted mb-1">{t('dateFilter.from')}</label>
                            <input
                                disabled={disabled}
                                className="form-control form-control-sm"
                                type="date"
                                value={customStartIso}
                                max={customEndIso || undefined}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (!val) return;
                                    setCustomStartIso(val);
                                }}
                            />
                        </div>
                        <div className="mb-2">
                            <label className="form-label fs-12 text-muted mb-1">{t('dateFilter.to')}</label>
                            <input
                                disabled={disabled}
                                className="form-control form-control-sm"
                                type="date"
                                value={customEndIso}
                                min={customStartIso || undefined}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (!val) return;
                                    setCustomEndIso(val);
                                }}
                            />
                        </div>
                        <Button
                            size="sm"
                            className="w-100"
                            disabled={disabled || !customStartIso || !customEndIso}
                            onClick={applyCustom}
                        >
                            {t('dateFilter.show')}
                        </Button>
                    </div>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default ReportDate;
